import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Plus, Trash2, Edit, LogOut, Package, ShoppingBag, Upload, X as XIcon, Check, Share2, MessageCircle, Wrench } from "lucide-react";
import { API } from "../lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const KIND_OPTIONS = [
  { value: "basico", label: "Colección Esencial ($24.990)" },
  { value: "clip1", label: "Clip Magnético — 1 clip ($34.990)" },
  { value: "clip5", label: "Clip Multicolor — 5 clips ($39.990)" },
  { value: "deportivo", label: "Deportivo ($39.990)" },
  { value: "flexit", label: "Flexit / Niños ($39.990)" },
  { value: "sol", label: "Lentes de sol" },
];

const CATEGORY_BY_KIND = {
  basico: "optico",
  clip1: "optico",
  clip5: "optico",
  deportivo: "deportivo",
  flexit: "flexit",
  sol: "sol",
};

const DEFAULT_PRICE_BY_KIND = {
  basico: 24990,
  clip1: 34990,
  clip5: 39990,
  deportivo: 39990,
  flexit: 39990,
  sol: 99990,
};

function emptyProduct() {
  return { brand: "", name: "", description: "Incluye estuche y paño", price: 24990, image: "", images: [], kind: "basico", category: "optico", active: true };
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [me, setMe] = useState(null);

  // estado admin
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({ name: "", detail: "", price: 0, priceFrom: false, quoteOnly: false, order: 0, active: true });

  // Confirmación universal (modal) — reemplaza a window.confirm que en móviles no siempre funciona
  const [confirmAsk, setConfirmAsk] = useState(null); // { title, description, onConfirm, danger }
  const askConfirm = (cfg) => setConfirmAsk(cfg);
  const [quoteFilter, setQuoteFilter] = useState("all"); // all | pending | completed
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct());

  // verificar si hay token activo al montar
  useEffect(() => {
    const token = localStorage.getItem("cdl_admin_token");
    if (token) {
      api.me()
        .then((r) => { setMe(r.data); setAuthed(true); })
        .catch(() => localStorage.removeItem("cdl_admin_token"));
    }
  }, []);

  useEffect(() => { if (authed) refreshAll(); }, [authed]);

  const refreshAll = async () => {
    try {
      const [p, q, c, s] = await Promise.all([api.listProducts(), api.listQuotes(), api.listCategories(), api.listServices()]);
      setProducts(p.data || []);
      setQuotes(q.data || []);
      setCategories(c.data || []);
      setServices(s.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.login(email, password);
      localStorage.setItem("cdl_admin_token", data.access_token);
      setMe({ id: data.id, email: data.email, name: data.name });
      setAuthed(true);
      toast.success("Bienvenido");
    } catch (err) {
      toast.error("Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("cdl_admin_token");
    setAuthed(false);
    setMe(null);
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p });
  };
  const startNew = () => {
    setEditing(null);
    setForm(emptyProduct());
  };
  const onKindChange = (kind) => {
    setForm((f) => ({ ...f, kind, category: CATEGORY_BY_KIND[kind], price: DEFAULT_PRICE_BY_KIND[kind] }));
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: Number(form.price) };
    try {
      if (editing) {
        await api.updateProduct(editing, payload);
        toast.success("Producto actualizado");
      } else {
        await api.createProduct(payload);
        toast.success("Producto creado");
      }
      startNew();
      refreshAll();
    } catch (err) {
      toast.error("Error al guardar");
    }
  };

  const deleteProduct = async (id) => {
    askConfirm({
      title: "¿Eliminar este producto?",
      description: "Esta acción no se puede deshacer.",
      danger: true,
      onConfirm: async () => {
        try {
          await api.deleteProduct(id);
          toast.success("Producto eliminado");
          refreshAll();
        } catch {
          toast.error("No se pudo eliminar");
        }
      },
    });
  };

  // ——— Acciones sobre cotizaciones ———
  const toggleQuoteStatus = async (q) => {
    const next = q.status === "completed" ? "pending" : "completed";
    try {
      await api.updateQuoteStatus(q.id, next);
      toast.success(next === "completed" ? "Cotización marcada como completada" : "Reabierta como pendiente");
      refreshAll();
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const removeQuote = async (id) => {
    askConfirm({
      title: "¿Eliminar esta cotización?",
      description: "Esta acción no se puede deshacer.",
      danger: true,
      onConfirm: async () => {
        try {
          await api.deleteQuote(id);
          toast.success("Cotización eliminada");
          refreshAll();
        } catch (e) {
          toast.error("No se pudo eliminar — verifica tu sesión");
        }
      },
    });
  };

  const buildQuoteText = (q) => {
    const lines = ["Cotización — La Clínica del Lente"];
    lines.push(`Cliente: ${q.name || "—"}`);
    if (q.phone) lines.push(`Tel: ${q.phone}`);
    lines.push("");
    (q.items || []).forEach((it, i) => {
      let line = `${i + 1}. ${it.brand ? it.brand + " — " : ""}${it.productName}`;
      if (it.price) line += ` ($${Number(it.price).toLocaleString("es-CL")} CLP)`;
      if (it.crystal) line += ` · Cristal: ${it.crystal}`;
      if (it.focal) line += ` · Focal: ${it.focal}`;
      if (it.color) line += ` · Color: ${it.color}`;
      if (it.receta) line += ` · Receta: ${it.receta}`;
      if (it.type === "reparacion") line += ` · Pago en efectivo`;
      lines.push(line);
    });
    if (q.subtotal) lines.push("", `Subtotal referencial: $${Number(q.subtotal).toLocaleString("es-CL")} CLP`);
    if (q.note) lines.push(`Nota: ${q.note}`);
    return lines.join("\n");
  };

  const shareQuote = async (q) => {
    const text = buildQuoteText(q);
    if (navigator.share) {
      try {
        await navigator.share({ title: "Cotización La Clínica del Lente", text });
        return;
      } catch {
        // usuario canceló → fallback
      }
    }
    const phone = (q.phone || "").replace(/\D/g, "");
    const targetPhone = phone || "56985754039";
    const url = `https://wa.me/${targetPhone}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyQuote = async (q) => {
    try {
      await navigator.clipboard.writeText(buildQuoteText(q));
      toast.success("Cotización copiada");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  // ——— Acciones sobre categorías ———
  const saveCategory = async (kind, data) => {
    try {
      await api.updateCategory(kind, { ...data, kind });
      toast.success("Categoría actualizada");
      refreshAll();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  // ——— Acciones sobre servicios ———
  const saveService = async (e) => {
    e.preventDefault();
    const payload = { ...serviceForm, price: Number(serviceForm.price), order: Number(serviceForm.order) };
    try {
      if (editingService) {
        await api.updateService(editingService, payload);
        toast.success("Servicio actualizado");
      } else {
        await api.createService(payload);
        toast.success("Servicio creado");
      }
      setEditingService(null);
      setServiceForm({ name: "", detail: "", price: 0, priceFrom: false, quoteOnly: false, order: services.length + 1, active: true });
      refreshAll();
    } catch {
      toast.error("Error al guardar servicio");
    }
  };

  const startEditService = (s) => {
    setEditingService(s.id);
    setServiceForm({ ...s });
  };

  const removeService = async (id) => {
    askConfirm({
      title: "¿Eliminar este servicio?",
      description: "Quedará oculto del sitio público de inmediato.",
      danger: true,
      onConfirm: async () => {
        try {
          await api.deleteService(id);
          toast.success("Servicio eliminado");
          refreshAll();
        } catch {
          toast.error("No se pudo eliminar");
        }
      },
    });
  };


  if (!authed) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-white border border-[#EAEAEA] p-10" data-testid="admin-login-form">
          <div className="h-[3px] w-full bg-gradient-to-r from-[#B8933A] via-[#E9C46A] to-[#B8933A] -mt-10 -mx-10 mb-8" />
          <h1 className="font-serif-display text-3xl text-[#1C1C1C] mb-2">Panel administrativo</h1>
          <p className="text-sm text-[#595959] mb-8">La Clínica del Lente</p>
          <div className="space-y-5">
            <div>
              <span className="eyebrow block mb-2">Email</span>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="minimal-input" data-testid="admin-email" />
            </div>
            <div>
              <span className="eyebrow block mb-2">Contraseña</span>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="minimal-input" data-testid="admin-password" />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-40" data-testid="admin-login-submit">
              {loading ? "Entrando..." : "Ingresar"}
            </button>
          </div>
        </form>
        <Toaster position="bottom-center" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="bg-[#1C1C1C] text-white" data-testid="admin-header">
        <div className="h-[3px] w-full bg-gradient-to-r from-[#B8933A] via-[#E9C46A] to-[#B8933A]" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div>
            <h1 className="font-serif-display text-2xl">Panel La Clínica del Lente</h1>
            <p className="text-xs text-[#b8b8b8] mt-1">Sesión: {me?.email}</p>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#E9C46A] hover:text-white" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex gap-6 mb-8 border-b border-[#EAEAEA] flex-wrap">
          <button onClick={() => setTab("products")} className={`flex items-center gap-2 pb-3 text-sm tracking-wide ${tab === "products" ? "text-[#1C1C1C] border-b-2 border-[#B8933A] font-semibold" : "text-[#595959]"}`} data-testid="tab-products">
            <Package className="w-4 h-4" /> Productos ({products.length})
          </button>
          <button onClick={() => setTab("categories")} className={`flex items-center gap-2 pb-3 text-sm tracking-wide ${tab === "categories" ? "text-[#1C1C1C] border-b-2 border-[#B8933A] font-semibold" : "text-[#595959]"}`} data-testid="tab-categories">
            <Edit className="w-4 h-4" /> Categorías ({categories.length})
          </button>
          <button onClick={() => setTab("services")} className={`flex items-center gap-2 pb-3 text-sm tracking-wide ${tab === "services" ? "text-[#1C1C1C] border-b-2 border-[#B8933A] font-semibold" : "text-[#595959]"}`} data-testid="tab-services">
            <Wrench className="w-4 h-4" /> Servicios ({services.length})
          </button>
          <button onClick={() => setTab("quotes")} className={`flex items-center gap-2 pb-3 text-sm tracking-wide ${tab === "quotes" ? "text-[#1C1C1C] border-b-2 border-[#B8933A] font-semibold" : "text-[#595959]"}`} data-testid="tab-quotes">
            <ShoppingBag className="w-4 h-4" /> Cotizaciones ({quotes.length})
          </button>
        </div>

        {tab === "products" && (
          <div className="grid lg:grid-cols-12 gap-10">
            <form onSubmit={saveProduct} className="lg:col-span-5 border border-[#EAEAEA] bg-white p-8 space-y-5 h-fit lg:sticky lg:top-6" data-testid="product-form">
              <h2 className="font-serif-display text-2xl">{editing ? "Editar producto" : "Nuevo producto"}</h2>
              <div>
                <span className="eyebrow block mb-2">Categoría / Colección</span>
                <select value={form.kind} onChange={(e) => onKindChange(e.target.value)} className="minimal-input" data-testid="form-kind">
                  {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </div>
              <div>
                <span className="eyebrow block mb-2">Marca</span>
                <input required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="minimal-input" data-testid="form-brand" />
              </div>
              <div>
                <span className="eyebrow block mb-2">Nombre del modelo</span>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="minimal-input" data-testid="form-name" />
              </div>
              <div>
                <span className="eyebrow block mb-2">Descripción</span>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="minimal-input" data-testid="form-description" />
              </div>
              <div>
                <span className="eyebrow block mb-2">Precio (CLP)</span>
                <input type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="minimal-input" data-testid="form-price" />
              </div>
              <div>
                <span className="eyebrow block mb-2">Imagen principal</span>
                <div className="flex items-center gap-3">
                  {form.image ? (
                    <div className="relative">
                      <img src={form.image.startsWith("http") ? form.image : `${process.env.REACT_APP_BACKEND_URL}${form.image}`} alt="principal" className="w-24 h-24 object-contain border border-[#EAEAEA] bg-[#FAF9F6]" />
                      <button type="button" onClick={() => setForm({ ...form, image: "" })} className="absolute -top-2 -right-2 bg-white border border-[#EAEAEA] p-1 hover:border-red-500 hover:text-red-500">
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ) : null}
                  <label className="btn-outline flex items-center gap-2 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Subir de galería
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const fd = new FormData();
                        fd.append("file", f);
                        try {
                          const r = await fetch(`${API}/uploads`, { method: "POST", body: fd });
                          const data = await r.json();
                          setForm((prev) => ({ ...prev, image: data.url }));
                          toast.success("Imagen principal subida");
                        } catch {
                          toast.error("Error al subir");
                        }
                      }}
                      data-testid="form-upload-main"
                    />
                  </label>
                </div>
                <input
                  value={form.image}
                  onChange={(e) => setForm({ ...form, image: e.target.value })}
                  placeholder="o pega una URL: https://..."
                  className="minimal-input mt-3"
                  data-testid="form-image"
                />
              </div>

              <div>
                <span className="eyebrow block mb-2">Imagen secundaria (opcional)</span>
                <div className="flex items-center gap-3 flex-wrap">
                  {(form.images || []).map((img, idx) => (
                    <div key={idx} className="relative">
                      <img src={img.startsWith("http") ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`} alt={`extra-${idx}`} className="w-20 h-20 object-contain border border-[#EAEAEA] bg-[#FAF9F6]" />
                      <button type="button" onClick={() => setForm({ ...form, images: form.images.filter((_, i) => i !== idx) })} className="absolute -top-2 -right-2 bg-white border border-[#EAEAEA] p-1 hover:border-red-500 hover:text-red-500">
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {(form.images || []).length < 3 && (
                    <label className="btn-outline flex items-center gap-2 cursor-pointer">
                      <Upload className="w-4 h-4" />
                      Agregar foto
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const fd = new FormData();
                          fd.append("file", f);
                          try {
                            const r = await fetch(`${API}/uploads`, { method: "POST", body: fd });
                            const data = await r.json();
                            setForm((prev) => ({ ...prev, images: [...(prev.images || []), data.url] }));
                            toast.success("Foto adicional subida");
                          } catch {
                            toast.error("Error al subir");
                          }
                        }}
                        data-testid="form-upload-extra"
                      />
                    </label>
                  )}
                </div>
                <p className="text-[11px] text-[#9A9A9A] mt-2">Puedes subir hasta 3 fotos adicionales para el mismo modelo.</p>
              </div>
              <div className="flex gap-2 pt-4">
                <button type="submit" className="btn-primary flex-1" data-testid="form-submit">
                  {editing ? "Guardar cambios" : "Crear producto"}
                </button>
                {editing && (
                  <button type="button" onClick={startNew} className="btn-outline">Cancelar</button>
                )}
              </div>
            </form>

            <div className="lg:col-span-7">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif-display text-2xl">Productos publicados</h2>
                <button onClick={startNew} className="btn-outline flex items-center gap-2" data-testid="new-product-btn">
                  <Plus className="w-4 h-4" /> Nuevo
                </button>
              </div>
              {products.length === 0 ? (
                <p className="text-sm text-[#595959] border border-dashed border-[#EAEAEA] p-10 text-center">
                  Aún no has publicado productos. Los valores por defecto del catálogo del sitio provienen del código.
                </p>
              ) : (
                <ul className="space-y-3">
                  {products.map((p) => (
                    <li key={p.id} className="flex items-center gap-4 border border-[#EAEAEA] bg-white p-4" data-testid={`admin-product-${p.id}`}>
                      <img src={p.image} alt={p.name} className="w-20 h-20 object-contain bg-[#FAF9F6]" />
                      <div className="flex-1 min-w-0">
                        <div className="eyebrow text-[10px] text-[#B8933A]">{p.brand}</div>
                        <div className="font-serif-display text-lg leading-tight">{p.name}</div>
                        <div className="text-xs text-[#595959] mt-1">{KIND_OPTIONS.find(k => k.value === p.kind)?.label || p.kind} · ${p.price?.toLocaleString("es-CL")}</div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => startEdit(p)} className="p-2 border border-[#EAEAEA] hover:border-[#1C1C1C]" data-testid={`edit-${p.id}`}>
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteProduct(p.id)} className="p-2 border border-[#EAEAEA] hover:border-red-500 hover:text-red-500" data-testid={`delete-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === "categories" && (
          <div className="space-y-5" data-testid="categories-panel">
            <p className="text-sm text-[#595959]">Edita los textos y precios destacados que aparecen en la portada de cada colección.</p>
            {categories.map((c) => (
              <CategoryEditor key={c.kind} category={c} onSave={saveCategory} />
            ))}
          </div>
        )}

        {tab === "services" && (
          <div className="grid lg:grid-cols-12 gap-10" data-testid="services-panel">
            <form onSubmit={saveService} className="lg:col-span-5 border border-[#EAEAEA] bg-white p-8 space-y-5 h-fit lg:sticky lg:top-6">
              <h2 className="font-serif-display text-2xl">{editingService ? "Editar servicio" : "Nuevo servicio"}</h2>
              <div>
                <span className="eyebrow block mb-2">Nombre del servicio</span>
                <input required value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} className="minimal-input" data-testid="service-form-name" />
              </div>
              <div>
                <span className="eyebrow block mb-2">Descripción / detalle</span>
                <textarea rows={3} value={serviceForm.detail} onChange={(e) => setServiceForm({ ...serviceForm, detail: e.target.value })} className="minimal-input" data-testid="service-form-detail" />
              </div>
              <div>
                <span className="eyebrow block mb-2">Precio (CLP)</span>
                <input type="number" value={serviceForm.price} onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })} className="minimal-input" data-testid="service-form-price" disabled={serviceForm.quoteOnly} />
              </div>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!serviceForm.priceFrom} onChange={(e) => setServiceForm({ ...serviceForm, priceFrom: e.target.checked })} data-testid="service-form-from" />
                  Mostrar como "Desde"
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!serviceForm.quoteOnly} onChange={(e) => setServiceForm({ ...serviceForm, quoteOnly: e.target.checked })} data-testid="service-form-quote-only" />
                  Sin precio fijo (se cotiza según medida)
                </label>
              </div>
              <div>
                <span className="eyebrow block mb-2">Orden</span>
                <input type="number" value={serviceForm.order} onChange={(e) => setServiceForm({ ...serviceForm, order: e.target.value })} className="minimal-input" data-testid="service-form-order" />
              </div>
              <div className="flex gap-2 pt-3">
                <button type="submit" className="btn-primary flex-1" data-testid="service-form-submit">
                  {editingService ? "Guardar cambios" : "Crear servicio"}
                </button>
                {editingService && (
                  <button type="button" onClick={() => { setEditingService(null); setServiceForm({ name: "", detail: "", price: 0, priceFrom: false, quoteOnly: false, order: services.length + 1, active: true }); }} className="btn-outline">Cancelar</button>
                )}
              </div>
            </form>

            <div className="lg:col-span-7 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-serif-display text-2xl">Servicios publicados</h2>
                <button onClick={() => { setEditingService(null); setServiceForm({ name: "", detail: "", price: 0, priceFrom: false, quoteOnly: false, order: services.length + 1, active: true }); }} className="btn-outline flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Nuevo
                </button>
              </div>
              {services.length === 0 ? (
                <p className="text-sm text-[#595959] border border-dashed border-[#EAEAEA] p-10 text-center">
                  Aún no hay servicios.
                </p>
              ) : services.map((s) => (
                <div key={s.id} className="border border-[#EAEAEA] bg-white p-5 flex items-center gap-4" data-testid={`admin-service-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-display text-lg leading-tight">{s.name}</div>
                    <div className="text-xs text-[#595959] line-clamp-2 mt-1">{s.detail}</div>
                    <div className="text-sm text-[#1C1C1C] font-medium mt-1">
                      {s.quoteOnly ? <span className="text-[#595959]">Se cotiza según medida</span> : (s.priceFrom ? `Desde $${Number(s.price).toLocaleString("es-CL")}` : `$${Number(s.price).toLocaleString("es-CL")}`)}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => startEditService(s)} className="p-2 border border-[#EAEAEA] hover:border-[#1C1C1C]" data-testid={`edit-service-${s.id}`}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => removeService(s.id)} className="p-2 border border-[#EAEAEA] hover:border-red-500 hover:text-red-500" data-testid={`delete-service-${s.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {tab === "quotes" && (
          <div className="space-y-4">
            {/* Filtros de estado */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="quote-filters">
              {[
                { v: "all", label: `Todas (${quotes.length})` },
                { v: "pending", label: `Pendientes (${quotes.filter(q => (q.status || "pending") === "pending").length})` },
                { v: "completed", label: `Completadas (${quotes.filter(q => q.status === "completed").length})` },
              ].map((f) => (
                <button
                  key={f.v}
                  onClick={() => setQuoteFilter(f.v)}
                  className={`chip ${quoteFilter === f.v ? "active" : ""}`}
                  data-testid={`filter-${f.v}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {(() => {
              const filtered = quotes.filter(q => {
                if (quoteFilter === "all") return true;
                const s = q.status || "pending";
                return s === quoteFilter;
              });
              if (filtered.length === 0) {
                return (
                  <p className="text-sm text-[#595959] border border-dashed border-[#EAEAEA] p-10 text-center">
                    {quotes.length === 0
                      ? "Todavía no hay cotizaciones guardadas desde el sitio."
                      : "No hay cotizaciones en esta categoría."}
                  </p>
                );
              }
              return filtered.map((q) => {
                const recetaUrls = (q.items || []).filter(i => i.fileUrl).map(i => i.fileUrl);
                const isCompleted = q.status === "completed";
                return (
                  <div key={q.id} className={`border ${isCompleted ? "border-[#B8933A] bg-[#E9C46A]/5" : "border-[#EAEAEA] bg-white"} p-6`} data-testid={`quote-${q.id}`}>
                    <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
                      <div className="flex-1 min-w-[180px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-serif-display text-xl text-[#1C1C1C]">{q.name || "Cliente sin nombre"}</span>
                          {isCompleted && (
                            <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase bg-[#B8933A] text-white px-2 py-0.5" data-testid={`quote-status-${q.id}`}>
                              <Check className="w-3 h-3" /> Completada
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[#595959] mt-1">{q.phone || "—"} · {new Date(q.created_at).toLocaleString("es-CL")}</div>
                      </div>
                      <div className="font-serif-display text-xl text-[#B8933A] shrink-0">
                        ${Number(q.subtotal || 0).toLocaleString("es-CL")}
                      </div>
                    </div>

                    <ul className="text-sm text-[#595959] space-y-2">
                      {(q.items || []).map((it, i) => {
                        const imgSrc = it.productImage
                          ? (it.productImage.startsWith("http") ? it.productImage : `${process.env.REACT_APP_BACKEND_URL}${it.productImage}`)
                          : null;
                        return (
                          <li key={i} className="flex gap-3 items-start">
                            {imgSrc ? (
                              <img src={imgSrc} alt={it.productName} className="w-14 h-14 object-contain bg-[#FAF9F6] border border-[#EAEAEA] shrink-0" />
                            ) : (
                              <div className="w-14 h-14 bg-[#F0F0EE] border border-[#EAEAEA] shrink-0 flex items-center justify-center text-[10px] text-[#9A9A9A] uppercase tracking-wide">
                                Sin foto
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div>{it.productName} {it.price ? <span className="text-[#1C1C1C] font-medium">${Number(it.price).toLocaleString("es-CL")}</span> : ""}</div>
                              {it.crystal && <div className="text-[11px] text-[#9A9A9A]">{it.crystal}{it.focal ? ` · ${it.focal}` : ""}{it.color ? ` · ${it.color}` : ""}{it.receta ? ` · Receta: ${it.receta}` : ""}</div>}
                              {it.type === "reparacion" && <div className="text-[11px] text-[#B8933A]">Pago en efectivo</div>}
                            </div>
                          </li>
                        );
                      })}
                    </ul>

                    {q.note && <p className="text-xs italic text-[#595959] mt-2">Nota: {q.note}</p>}

                    {recetaUrls.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-[#EAEAEA]">
                        <span className="eyebrow text-[10px] text-[#B8933A] block mb-2">Recetas adjuntas</span>
                        <div className="flex flex-wrap gap-3">
                          {recetaUrls.map((u, i) => {
                            const fullUrl = u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
                            const isPdf = u.toLowerCase().endsWith(".pdf");
                            return isPdf ? (
                              <a key={i} href={fullUrl} target="_blank" rel="noreferrer" className="btn-outline flex items-center gap-2 text-xs" data-testid={`quote-receta-${q.id}-${i}`}>
                                📄 Ver PDF receta
                              </a>
                            ) : (
                              <a key={i} href={fullUrl} target="_blank" rel="noreferrer" className="block border border-[#EAEAEA] hover:border-[#B8933A] transition-colors" data-testid={`quote-receta-${q.id}-${i}`}>
                                <img src={fullUrl} alt="Receta cliente" className="w-32 h-32 object-cover" />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Acciones */}
                    <div className="flex items-center gap-2 flex-wrap mt-5 pt-4 border-t border-[#EAEAEA]">
                      <button
                        onClick={() => toggleQuoteStatus(q)}
                        className={`flex items-center gap-2 px-3 py-2 text-xs tracking-[0.15em] uppercase font-semibold border transition-colors ${isCompleted ? "border-[#EAEAEA] text-[#595959] hover:border-[#1C1C1C] hover:text-[#1C1C1C]" : "bg-[#1C1C1C] text-white border-[#1C1C1C] hover:bg-[#B8933A] hover:border-[#B8933A]"}`}
                        data-testid={`quote-complete-${q.id}`}
                      >
                        <Check className="w-4 h-4" />
                        {isCompleted ? "Reabrir" : "Completar"}
                      </button>

                      <button
                        onClick={() => shareQuote(q)}
                        className="flex items-center gap-2 px-3 py-2 text-xs tracking-[0.15em] uppercase font-semibold border border-[#25D366] text-[#1C1C1C] hover:bg-[#25D366] hover:text-white transition-colors"
                        data-testid={`quote-share-${q.id}`}
                      >
                        <Share2 className="w-4 h-4" />
                        Compartir
                      </button>

                      {q.phone && (
                        <a
                          href={`https://wa.me/${q.phone.replace(/\D/g, "")}?text=${encodeURIComponent("Hola " + (q.name || "") + ", te escribo de La Clínica del Lente sobre tu cotización.")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 px-3 py-2 text-xs tracking-[0.15em] uppercase font-semibold border border-[#EAEAEA] text-[#1C1C1C] hover:border-[#1C1C1C] transition-colors"
                          data-testid={`quote-whatsapp-${q.id}`}
                        >
                          <MessageCircle className="w-4 h-4" />
                          WhatsApp cliente
                        </a>
                      )}

                      <button
                        onClick={() => copyQuote(q)}
                        className="flex items-center gap-2 px-3 py-2 text-xs tracking-[0.15em] uppercase font-semibold border border-[#EAEAEA] text-[#595959] hover:border-[#1C1C1C] hover:text-[#1C1C1C] transition-colors"
                        data-testid={`quote-copy-${q.id}`}
                      >
                        Copiar texto
                      </button>

                      <button
                        onClick={() => removeQuote(q.id)}
                        className="ml-auto flex items-center gap-2 px-3 py-2 text-xs tracking-[0.15em] uppercase font-semibold border border-[#EAEAEA] text-[#595959] hover:border-red-500 hover:text-red-500 transition-colors"
                        data-testid={`quote-delete-${q.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Eliminar
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      <Toaster position="bottom-center" />

      {/* Confirmación universal */}
      <AlertDialog open={!!confirmAsk} onOpenChange={(o) => !o && setConfirmAsk(null)}>
        <AlertDialogContent className="bg-[#FAF9F6] border border-[#EAEAEA] rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl text-[#1C1C1C]">
              {confirmAsk?.title || "Confirmar"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#595959]">
              {confirmAsk?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => { const fn = confirmAsk?.onConfirm; setConfirmAsk(null); if (fn) await fn(); }}
              className={`rounded-none ${confirmAsk?.danger ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#1C1C1C] hover:bg-[#333] text-white"}`}
              data-testid="confirm-action"
            >
              {confirmAsk?.danger ? "Eliminar" : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CategoryEditor({ category, onSave }) {
  const [form, setForm] = useState(category);
  useEffect(() => { setForm(category); }, [category]);
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave(category.kind, form); }}
      className="border border-[#EAEAEA] bg-white p-6 grid md:grid-cols-2 gap-4"
      data-testid={`category-editor-${category.kind}`}
    >
      <div className="md:col-span-2 flex items-baseline gap-3">
        <span className="eyebrow text-[#B8933A]">{category.kind}</span>
        <span className="text-xs text-[#9A9A9A]">Edita los textos visibles en la portada de esta colección</span>
      </div>
      <div>
        <span className="eyebrow block mb-2">Eyebrow (categoría pequeña)</span>
        <input value={form.eyebrow || ""} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} className="minimal-input" />
      </div>
      <div>
        <span className="eyebrow block mb-2">Precio destacado (texto)</span>
        <input value={form.priceTag || ""} onChange={(e) => setForm({ ...form, priceTag: e.target.value })} placeholder="$24.990" className="minimal-input" />
      </div>
      <div className="md:col-span-2">
        <span className="eyebrow block mb-2">Título principal</span>
        <input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="minimal-input" />
      </div>
      <div className="md:col-span-2">
        <span className="eyebrow block mb-2">Descripción</span>
        <textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="minimal-input" />
      </div>
      <div className="md:col-span-2 flex justify-end">
        <button type="submit" className="btn-primary" data-testid={`category-save-${category.kind}`}>Guardar</button>
      </div>
    </form>
  );
}
